<template>
  <div>
    <v-data-table
      :headers="tableHeaders"
      :items="filteredBudgets"
      class="p-no-borders p-table-filled"
      disable-pagination
      hide-default-footer
    >
      <template v-slot:[`header.category`]="{ header }">
        {{ header.text }}
        <v-tooltip v-if="hasAccess()" bottom slot="append-outer">
          <template v-slot:activator="{ on }">
            <router-link :to="{ name: 'settingsBudgetCategories' }">
              <v-icon class="ml-2" v-on="on">mdi-cog-outline</v-icon>
            </router-link>
          </template>
          {{ $t('budget.manageCategories') }}
        </v-tooltip>
      </template>
      <template v-slot:[`item.category`]="{ item }">
        <span class="font-weight-medium">{{ budgetCategoryById(item.budgetCategoryId).name }}</span>
      </template>
      <template v-slot:[`item.name`]="{ item }">
        <v-edit-dialog
          v-if="hasAccess()"
          :return-value.sync="inlineEditBudget.name"
          @open="inlineEditOpen(item)"
          @save="inlineEditSave(false)"
        >
          {{ item.name }}
          <template v-slot:input>
            <v-text-field
              v-model="inlineEditBudget.name"
              :rules="[rule.required()]"
            ></v-text-field>
          </template>
        </v-edit-dialog>
        <template v-else>{{ item.name }}</template>
      </template>
      <template v-slot:[`item.quantity`]="{ item }">
        <v-edit-dialog
          v-if="hasAccess()"
          :return-value.sync="inlineEditBudget.quantity"
          @open="hasAccess() ? inlineEditOpen(item) : null"
          @save="inlineEditSave(true)"
        >
          {{ item.quantity }}
          <template v-slot:input>
            <p-number-input
              v-model="inlineEditBudget.quantity"
              :rules="[rule.required()]"
              integer
            ></p-number-input>
          </template>
        </v-edit-dialog>
        <template v-else>{{ item.quantity }}</template>
      </template>
      <template v-slot:[`item.amount`]="{ item }">
        <v-edit-dialog
          v-if="hasAccess()"
          :return-value.sync="inlineEditBudget.amount"
          @open="inlineEditOpen(item)"
          @save="inlineEditSave(true)"
        >
          <p-currency :value="item.amount"></p-currency>
          <template v-slot:input>
            <p-number-input
              v-model="inlineEditBudget.amount"
              :rules="[rule.required()]"
            ></p-number-input>
          </template>
        </v-edit-dialog>
        <p-currency v-else :value="item.amount"></p-currency>
      </template>
      <template v-slot:[`item.total`]="{ item }">
        <p-currency :value="item.total"></p-currency>
      </template>
      <template v-if="hasAccess()" v-slot:[`item.actions`]="{ item }">
        <div class="p-table-row-actions p-visible-on-row-hover">
          <p-btn-icon type="edit" @click="openBudgetEditDialog(item)"></p-btn-icon>
          <p-btn-icon type="delete" @click="deleteBudget_(item.id)"></p-btn-icon>
        </div>
      </template>
      <template v-if="hasAccess()" v-slot:footer>
        <v-divider></v-divider>
        <div class="d-flex justify-space-between align-center py-2">
          <v-btn
            color="primary"
            class="p-btn-text"
            text
            @click="openBudgetEditDialog(null)"
          >
            {{ income ? $t('budget.addIncome') : $t('budget.addExpense') }}
          </v-btn>
          <div class="font-weight-medium pr-5">
            <span class="text--secondary pr-6">{{ $t('budget.table.total') }}</span>
            <p-currency
              :value="budgetsTotal"
              display-currency
            ></p-currency>
          </div>
        </div>
      </template>
    </v-data-table>

    <budget-edit-dialog
      :dialog.sync="budgetEditDialog"
      :budget="editBudget"
      :income="income"
      @saved="$emit('change')"
    ></budget-edit-dialog>

  </div>
</template>


<script>
import { mapGetters, mapActions } from 'vuex'

import BudgetEditDialog from './BudgetEditDialog'
import PNumberInput from '@/components/PNumberInput'
import PBtnIcon from '@/components/PBtnIcon'
import PCurrency from '@/components/PCurrency'

import snackbarMixin from '@/mixins/snackbar.mixin'
import objectMixin from '@/mixins/object.mixin'
import validationMixin from '@/mixins/validation.mixin'
import accessMixin from '@/mixins/access.mixin'


export default {
  components: {
    BudgetEditDialog,
    PNumberInput,
    PBtnIcon,
    PCurrency
  },


  mixins: [
    snackbarMixin,
    objectMixin,
    validationMixin,
    accessMixin
  ],


  props: {
    income: Boolean
  },


  data () {
    return {
      budgetEditDialog: false,
      editBudget: null,
      inlineEditBudget: {}
    }
  },


  computed: {
    ...mapGetters('budget', ['budgets']),
    ...mapGetters('budgetCategory', ['budgetCategoryById']),

    tableHeaders () {
      return [
        { text: this.income ? this.$t('budget.table.incomeCategory') : this.$t('budget.table.expenseCategory'), value: 'category', sortable: false },
        { text: this.$t('budget.table.title'), value: 'name' },
        { text: this.$t('budget.table.quantity'), value: 'quantity', align: 'end' },
        { text: this.$t('budget.table.amount'), value: 'amount', align: 'end' },
        { text: this.$t('budget.table.total'), value: 'total', align: 'end' },
        { text: '', value: 'actions', align: 'end', width: 75, sortable: false, filterable: false }
      ]
    },

    filteredBudgets () {
      return this.budgets.filter(x => this.budgetCategoryById(x.budgetCategoryId).income === this.income)
    },

    budgetsTotal () {
      return this.filteredBudgets.reduce((sum, budget) => {
        return sum + budget.total
      }, 0)
    }
  },


  methods: {
    ...mapActions('budget', ['updateBudget', 'deleteBudget']),

    openBudgetEditDialog (budget) {
      this.editBudget = budget
      this.budgetEditDialog = true
    },

    inlineEditOpen (budget) {
      this.inlineEditBudget = this.getObjectCopy(budget)
    },

    async inlineEditSave (emitChange) {
      try {
        await this.updateBudget(this.inlineEditBudget)
        if (emitChange) {
          this.$emit('change')
        }
      } catch (error) {
        this.showSnackbarError()
        this.$log.error(error)
      }
    },

    async deleteBudget_ (id) {
      try {
        const confirm = await this.$confirm(this.$t('budget.deleteBudgetConfirm'), { trueText: this.$t('delete') })
        if (confirm === true) {
          this.showSnackbarProcessing()
          await this.deleteBudget(id)
          this.showSnackbarSuccess(this.$t('budget.budgetDeleted'))
          this.$emit('change')
        }
      } catch (error) {
        this.showSnackbarError()
        this.$log.error(error)
      }
    }
  }
}
</script>
